<script setup>
// NOTICE: KAPTION-INTEGRATION
// desktop-notifications-nudge.BANNER.1..6
//
// Desktop-notification nudge. Agents who never granted browser permission get
// no desktop alerts and usually do not know it — nothing in the product says
// so. This ribbon says so, at the top of the dashboard, and grants permission
// in one click.
//
// Two independent switches gate it, and both must be on:
//   - KAPTION_DESKTOP_NOTIFICATIONS_NUDGE, the kill switch (default ON)
//   - the account's desktop_notifications_prompt, which an admin opts into
//     in Settings > General (administrator-gated route)
//
// Deliberately not dismissible, matching KaptionWhatsapp2faBanner: it clears
// the moment the underlying state is fixed (permission granted), so a dismiss
// control would only let the problem persist invisibly. The admin toggle is
// the escape hatch, account-wide.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Banner from 'dashboard/components/ui/Banner.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { requestPushPermissions } from 'dashboard/helper/pushHelper.js';

const { t } = useI18n();
const { currentAccount } = useAccount();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const hasPushAPISupport = () => 'Notification' in window;

const readPermission = () =>
  hasPushAPISupport() ? Notification.permission : null;

// Notification.permission is a plain string, not reactive, so mirror it into
// state we control and re-read it once the browser prompt resolves. Seeded
// synchronously rather than in onMounted so the ribbon is present on the
// first paint instead of appearing a frame later.
const permission = ref(readPermission());

const syncPermission = () => {
  permission.value = readPermission();
};

// desktop-notifications-nudge.ACCOUNT.3 — the admin opt-in.
const isEnabledForAccount = computed(
  () => !!currentAccount.value?.settings?.desktop_notifications_prompt
);

const isActive = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_DESKTOP_NOTIFICATIONS_NUDGE') &&
    isEnabledForAccount.value &&
    hasPushAPISupport()
);

// 'default' = never asked. This is the one state where the browser will still
// show its prompt, so it is the only state with an action button.
const shouldPrompt = computed(
  () => isActive.value && permission.value === 'default'
);

// 'denied' is not a dead end we can hide: a browser will not re-prompt, and
// the user has no way of knowing that is why they hear nothing. Show them, and
// tell them where the switch actually lives — but offer no button, because
// requestPermission() would resolve denied without showing anything.
const isBlocked = computed(
  () => isActive.value && permission.value === 'denied'
);

const shouldShowBanner = computed(() => shouldPrompt.value || isBlocked.value);

const enableNotifications = () => {
  requestPushPermissions({ onSuccess: syncPermission });
  // The callback only fires on success; re-read regardless so a dismissal of
  // the browser prompt ('denied') also lands in state.
  setTimeout(syncPermission, 0);
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShowBanner"
    :color-scheme="isBlocked ? 'alert' : 'warning'"
    :banner-message="
      isBlocked
        ? t('KAPTION_DESKTOP_NOTIFICATIONS.BANNER.BLOCKED_MESSAGE')
        : t('KAPTION_DESKTOP_NOTIFICATIONS.BANNER.MESSAGE')
    "
    :action-button-label="t('KAPTION_DESKTOP_NOTIFICATIONS.BANNER.ACTION')"
    action-button-icon="i-lucide-bell-ring"
    :has-action-button="shouldPrompt"
    @primary-action="enableNotifications"
  />
</template>
